import { useEffect, useState } from 'react'
import {
  RefreshCw, Landmark, Users, BarChart3, Brain,
  Newspaper, TrendingUp, TrendingDown, AlertTriangle,
  ExternalLink, ChevronRight, Shield, Zap, Plane,
  Lightbulb, DollarSign, ArrowUpDown,
  Activity, Eye, Anchor,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts'
import { useAltData } from '@/hooks/useAltData'
import TickerLink from '@/components/ui/TickerLink'
import { format, formatDistanceToNow } from 'date-fns'
import { safeFormat } from '../lib/format'

/* ── helpers ─────────────────────────────────────────────────────────────── */

function fmtUsd(n: number | null | undefined) {
  if (n == null || !Number.isFinite(n)) return '$0'
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
  return `$${n.toLocaleString()}`
}

function fearGreedColor(v: number) {
  if (v <= 25) return '#f43f5e'
  if (v <= 45) return '#f59e0b'
  if (v <= 55) return '#94a3b8'
  if (v <= 75) return '#22c55e'
  return '#10b981'
}

function riskColor(level: string | null | undefined) {
  const l = (level ?? '').toUpperCase()
  if (l === 'LOW') return 'text-emerald bg-emerald/10'
  if (l === 'MODERATE') return 'text-amber bg-amber/10'
  return 'text-rose-light bg-rose/10'
}

function signalBadge(strength: string) {
  if (strength === 'high') return 'badge-red'
  if (strength === 'medium') return 'badge-yellow'
  return 'badge-blue'
}

/* ── source-failure banner ───────────────────────────────────────────────── */

function AltDataFailureBanner({ failures, total, onRetry, retrying }: {
  failures: Array<{ source: string; reason: string }>; total: number
  onRetry: () => void; retrying: boolean
}) {
  const [open, setOpen] = useState(false)
  // Collapse identical error reasons (e.g. a server-wide rate-limit hits many
  // sources with the same message) so the list reads at a glance.
  const byReason = new Map<string, string[]>()
  for (const f of failures) {
    const arr = byReason.get(f.reason) ?? []
    arr.push(f.source)
    byReason.set(f.reason, arr)
  }
  return (
    <div className="rounded-lg border border-amber/40 bg-amber/10 px-4 py-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm text-amber-light font-medium w-full text-left"
      >
        <AlertTriangle size={15} aria-hidden />
        {failures.length} of {total} data sources failed to load
        <ChevronRight size={14} className={open ? 'rotate-90 transition-transform' : 'transition-transform'} aria-hidden />
      </button>
      <p className="text-xs text-text-muted mt-1">
        Most sources are live external APIs (SEC, Yahoo, Google Trends) that get
        rate-limited on the server IP. Panels below stay empty until they recover.
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                   bg-amber/15 text-amber-light border border-amber/40
                   hover:bg-amber/25 transition-colors disabled:opacity-50 text-xs font-medium"
      >
        <RefreshCw size={12} className={retrying ? 'animate-spin' : ''} aria-hidden />
        {retrying ? 'Retrying…' : 'Retry failed sources'}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 max-h-64 overflow-auto pr-1">
          {[...byReason.entries()].map(([reason, sources]) => (
            <div key={reason} className="text-xs rounded bg-rose/10 border border-rose/30 px-2 py-1.5">
              <div className="text-rose-light font-medium">{sources.join(', ')}</div>
              <div className="text-text-muted break-words font-mono mt-0.5">{reason}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── section wrapper ─────────────────────────────────────────────────────── */

function Section({ icon: Icon, title, children }: {
  icon: React.ElementType; title: string; children: React.ReactNode
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-blue/10 border border-blue/20 flex items-center justify-center">
          <Icon size={14} className="text-blue-light" aria-hidden />
        </div>
        <h2 className="panel-title !text-xs">{title}</h2>
      </div>
      {children}
    </section>
  )
}

/* ── guru tabs ───────────────────────────────────────────────────────────── */

// 13F holdings carry issuer NAMES (no tickers), so rows render plain text —
// fabricating ticker links from names would be dishonest guessing.
function GuruTabs({ portfolios }: { portfolios: Array<{ fund_name: string; manager: string; top_holdings?: Array<{ name: string; value: number | null; portfolio_pct: number | null }> }> }) {
  const [active, setActive] = useState(0)
  if (!portfolios.length) return <p className="text-sm text-text-muted">No guru data available.</p>
  const guru = portfolios[Math.min(active, portfolios.length - 1)]
  return (
    <div className="card p-4">
      <div className="flex flex-wrap gap-1.5 mb-4">
        {portfolios.map((g, i) => (
          <button
            key={g.fund_name || g.manager}
            onClick={() => setActive(i)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              i === active
                ? 'bg-blue/20 text-blue-light'
                : 'text-text-secondary hover:bg-surface-hover'
            }`}
          >
            {g.manager?.split(' ').pop() ?? g.fund_name}
          </button>
        ))}
      </div>
      <p className="text-xs text-text-muted mb-3">{guru.fund_name} &mdash; {guru.manager}</p>
      <div className="space-y-2">
        {(guru.top_holdings ?? []).slice(0, 10).map((h, i) => (
          <div key={i} className="flex items-center justify-between text-sm gap-3">
            <span className="text-text-primary truncate">{h.name || '—'}</span>
            <div className="flex items-center gap-4 shrink-0">
              <span className="text-text-secondary">{h.value != null ? fmtUsd(h.value) : '—'}</span>
              <span className="text-text-muted text-xs w-12 text-right">
                {h.portfolio_pct != null ? `${h.portfolio_pct.toFixed(1)}%` : '—'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── main page ───────────────────────────────────────────────────────────── */

export default function AltDataPage() {
  const store = useAltData()
  const {
    loading, lastFetched, fetchAll, failures, totalSources, stale, dataAsOf,
    congressTrades, congressTopBought, congressTopSold, govContracts,
    insiderLarge, insiderClusters, guruPortfolios,
    fedIndicators, yieldCurve, recessionSignals, fomcMeetings,
    fearGreedValue, fearGreedLabel, wsbPosts, trendingTickers,
    headlines, materialEvents,
    mostShorted, squeezeCandidates,
    etfInflows, etfOutflows, sectorRotation,
    lobbyingSpenders, topInnovators, unusualFlights,
    unusualOptions, putCallRatio, darkPoolMostActive,
    whaleMoves, exchangeFlows,
    googleTrending, cramerPicks, recentSplits,
  } = store

  useEffect(() => { fetchAll() }, []) // eslint-disable-line

  const nextFomc = fomcMeetings.length ? fomcMeetings[0] : null

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Alternative Data Intelligence</h1>
          <p className="text-sm text-text-secondary mt-1">
            Congressional trades, insider moves, Google Trends, Cramer picks, stock splits, macro signals, sentiment &amp; more
          </p>
          {lastFetched && (
            <p className="text-xs text-text-muted mt-0.5">
              Last updated: {format(lastFetched, 'HH:mm, d MMM yyyy')}
            </p>
          )}
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="btn-secondary text-xs gap-1.5"
          aria-label="Refresh alt data"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Stale badge — the latest fetch came back empty (sources down) so the
          panels below are the last-good cached payload, not live. */}
      {stale && dataAsOf && (
        <div className="rounded-lg border border-blue/40 bg-blue/10 px-4 py-2 text-xs text-blue-light flex items-center gap-2">
          <RefreshCw size={13} aria-hidden />
          Showing last-good cached data from {safeFormat(dataAsOf, 'HH:mm, d MMM yyyy', format)} — the latest
          refresh returned nothing (sources rate-limited). Retry below or check back shortly.
        </div>
      )}

      {/* Source-failure banner — makes an empty page debuggable instead of
          silently blank (most sources are live external APIs that get
          rate-limited on the server IP). */}
      {!loading && failures.length > 0 && (
        <AltDataFailureBanner failures={failures} total={totalSources} onRetry={fetchAll} retrying={loading} />
      )}

      {/* ─── Options Flow ────────────────────────────────────────────────── */}
      <Section icon={Activity} title="Options Flow Intelligence">
        <div className="grid lg:grid-cols-2 gap-4 stagger-children">
          {/* Put/Call Ratio Gauge */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Put/Call Ratio</h3>
            {putCallRatio && putCallRatio.total_pcr != null ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-text-primary">{putCallRatio.total_pcr}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-semibold ${
                      putCallRatio.sentiment === 'BULLISH' ? 'bg-emerald/10 text-emerald' :
                      putCallRatio.sentiment === 'BEARISH' ? 'bg-rose/10 text-rose-light' :
                      'bg-amber/10 text-amber'
                    }`}>{putCallRatio.sentiment}</span>
                  </div>
                  {/* Backend sends volume_pcr / oi_pcr (SPY chain), not equity/index PCRs. */}
                  <div className="text-right text-xs text-text-muted space-y-1">
                    <p>Volume: {putCallRatio.volume_pcr ?? '—'}</p>
                    <p>Open interest: {putCallRatio.oi_pcr ?? '—'}</p>
                  </div>
                </div>
                {/* PCR bar indicator */}
                <div className="relative h-3 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    className={`absolute h-full rounded-full transition-all ${
                      putCallRatio.total_pcr < 0.7 ? 'bg-emerald' :
                      putCallRatio.total_pcr > 1.3 ? 'bg-rose-light' : 'bg-amber'
                    }`}
                    style={{ width: `${Math.min(100, (putCallRatio.total_pcr / 2) * 100)}%` }}
                  />
                  {/* Reference marks */}
                  <div className="absolute top-0 h-full w-px bg-text-muted/30" style={{ left: '35%' }} title="0.7 Bullish" />
                  <div className="absolute top-0 h-full w-px bg-text-muted/30" style={{ left: '65%' }} title="1.3 Bearish" />
                </div>
                <div className="flex justify-between text-[10px] text-text-muted">
                  <span>Bullish (&lt;0.7)</span>
                  <span>Neutral</span>
                  <span>Bearish (&gt;1.3)</span>
                </div>
                <p className="text-xs text-text-secondary">{putCallRatio.interpretation}</p>
              </div>
            ) : (
              <p className="text-sm text-text-muted">No put/call ratio data available.</p>
            )}
          </div>

          {/* Unusual Options Activity */}
          <div className="card p-4 overflow-x-auto">
            <h3 className="panel-title mb-3">Unusual Options Activity</h3>
            {unusualOptions.length === 0 ? (
              <p className="text-sm text-text-muted">No unusual options activity detected.</p>
            ) : (
              <table className="w-full text-xs table-pro">
                <thead>
                  <tr>
                    <th>Ticker</th>
                    <th>Type</th>
                    <th className="!text-right">Strike</th>
                    <th className="!text-right">Premium</th>
                    <th>Exp</th>
                    <th>Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {unusualOptions.slice(0, 15).map((t, i) => (
                    <tr key={i}>
                      <td className="py-1.5"><TickerLink ticker={t.ticker} /></td>
                      <td className={`py-1.5 ${t.type === 'CALL' ? 'text-emerald' : 'text-rose-light'}`}>{t.type}</td>
                      <td className="py-1.5 text-right text-text-secondary">${t.strike}</td>
                      <td className="py-1.5 text-right text-text-primary font-medium">{fmtUsd(t.total_premium)}</td>
                      <td className="py-1.5 text-text-muted">{t.expiration?.slice(5)}</td>
                      <td className="py-1.5">
                        <span className={`badge text-[10px] ${t.sentiment === 'BULLISH' ? 'badge-green' : 'badge-red'}`}>
                          {t.sentiment}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Section>

      {/* ─── Dark Pool ─────────────────────────────────────────────────────── */}
      <Section icon={Eye} title="Dark Pool Intelligence">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Most Active Dark Pool */}
          <div className="card p-4 overflow-x-auto lg:col-span-2">
            <h3 className="panel-title mb-3">Most Active Dark Pool Tickers</h3>
            {darkPoolMostActive.length === 0 ? (
              <p className="text-sm text-text-muted">No dark pool data available.</p>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {darkPoolMostActive.slice(0, 10).map((t, i) => {
                  const change = t.vs_20d_avg
                  return (
                    <div key={i} className="p-3 rounded-lg bg-surface-hover/50 border border-border/50">
                      <div className="flex items-center justify-between mb-1">
                        <TickerLink ticker={t.ticker} className="text-sm" />
                        <span className={`text-xs font-semibold ${
                          t.signal === 'ELEVATED' ? 'text-amber' : 'text-text-muted'
                        }`}>{t.signal}</span>
                      </div>
                      <p className="text-lg font-bold text-text-primary">{(t.dark_pool_pct ?? 0).toFixed(1)}%</p>
                      <div className="flex items-center gap-1 text-xs mt-1">
                        <span className="text-text-muted">vs 20d avg:</span>
                        <span className={change > 0 ? 'text-rose-light' : change < 0 ? 'text-emerald' : 'text-text-muted'}>
                          {(change ?? 0) > 0 ? '+' : ''}{(change ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <p className="text-[10px] text-text-muted mt-1">{t.sector}</p>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ─── Whale Alerts ──────────────────────────────────────────────────── */}
      <Section icon={Anchor} title="Whale Alerts &amp; Exchange Flows">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Exchange Flows Summary */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Exchange Flows</h3>
            {exchangeFlows ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      exchangeFlows.overall_signal === 'BULLISH' ? 'bg-emerald/10 text-emerald' :
                      exchangeFlows.overall_signal === 'BEARISH' ? 'bg-rose/10 text-rose-light' :
                      'bg-amber/10 text-amber'
                    }`}>{exchangeFlows.overall_signal}</span>
                  </div>
                  <div className="text-right text-xs text-text-muted">
                    <p>Net: {fmtUsd(Math.abs(exchangeFlows.net_flow_usd))} {exchangeFlows.net_flow_usd > 0 ? 'inflow' : 'outflow'}</p>
                  </div>
                </div>
                <p className="text-xs text-text-secondary">{exchangeFlows.interpretation}</p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {exchangeFlows.exchanges.slice(0, 8).map((ex, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-text-primary">{ex.exchange}</span>
                      <div className="flex items-center gap-2">
                        <span className={ex.net_flow_usd > 0 ? 'text-rose-light' : 'text-emerald'}>
                          {fmtUsd(Math.abs(ex.net_flow_usd))} {(ex.signal ?? '').toLowerCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-text-muted">No exchange flow data available.</p>
            )}
          </div>

          {/* Large Crypto Moves */}
          <div className="card p-4 overflow-x-auto">
            <h3 className="panel-title mb-3">Large Crypto Transfers</h3>
            {whaleMoves.length === 0 ? (
              <p className="text-sm text-text-muted">No whale moves detected.</p>
            ) : (
              <table className="w-full text-xs table-pro">
                {/* No Signal column: the backend whale-move payload has no
                    `signal` field — rendering one would fabricate a read. */}
                <thead>
                  <tr>
                    <th>Coin</th>
                    <th className="!text-right">USD Value</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  {whaleMoves.slice(0, 12).map((m, i) => (
                    <tr key={i}>
                      <td className="py-1.5 font-mono text-blue-light">{m.coin}</td>
                      <td className="py-1.5 text-right text-text-primary font-medium">{fmtUsd(m.amount_usd)}</td>
                      <td className="py-1.5 text-text-secondary truncate max-w-[100px]">{m.from}</td>
                      <td className="py-1.5 text-text-secondary truncate max-w-[100px]">{m.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Section>

      {/* ─── Short Interest ──────────────────────────────────────────────── */}
      <Section icon={Shield} title="Short Interest Intelligence">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Most shorted table */}
          <div className="card p-4 overflow-x-auto">
            <h3 className="panel-title mb-3">Most Shorted Stocks</h3>
            {mostShorted.length === 0 ? (
              <p className="text-sm text-text-muted">No short interest data available.</p>
            ) : (
              <table className="w-full text-sm table-pro">
                <thead>
                  <tr>
                    <th>Ticker</th>
                    <th>Company</th>
                    <th className="!text-right">Short %</th>
                    <th className="!text-right">Days to Cover</th>
                    <th className="!text-right">Sector</th>
                  </tr>
                </thead>
                <tbody>
                  {mostShorted.slice(0, 15).map((s, i) => (
                    <tr key={i}>
                      <td className="py-2 pr-3"><TickerLink ticker={s.ticker} /></td>
                      <td className="py-2 pr-3 text-text-primary whitespace-nowrap">{s.company}</td>
                      <td className="py-2 pr-3 text-right">
                        <span className={`font-medium ${s.short_pct >= 30 ? 'text-rose-light' : s.short_pct >= 20 ? 'text-amber' : 'text-text-secondary'}`}>
                          {s.short_pct?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right text-text-secondary">{s.days_to_cover?.toFixed(1)}</td>
                      <td className="py-2 text-right text-text-muted text-xs">{s.sector}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Squeeze candidates */}
          <div className="card p-4">
            <h3 className="panel-title mb-3 flex items-center gap-1.5">
              <Zap size={13} className="text-amber" aria-hidden /> Short Squeeze Candidates
            </h3>
            <p className="text-xs text-text-muted mb-3">High short interest + high days to cover = potential squeeze</p>
            {squeezeCandidates.length === 0 ? (
              <p className="text-sm text-text-muted">No squeeze candidates detected.</p>
            ) : (
              <div className="space-y-3">
                {squeezeCandidates.slice(0, 8).map((c, i) => (
                  <div key={i} className="p-3 rounded-lg bg-amber/5 border border-amber/20">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <TickerLink ticker={c.ticker} className="font-semibold" />
                        <span className="text-text-secondary text-xs">{c.company}</span>
                      </div>
                      <span className="text-amber text-xs font-bold">Score: {c.squeeze_score}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-text-muted mt-1">
                      <span>Short: {c.short_pct != null ? `${c.short_pct.toFixed(1)}%` : '—'}</span>
                      <span>DTC: {c.days_to_cover != null ? c.days_to_cover.toFixed(1) : '—'}</span>
                      {/* Backend sends float_shares (raw share count), not float_m */}
                      <span>Float: {c.float_shares != null ? `${(c.float_shares / 1e6).toFixed(0)}M` : '—'}</span>
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      {c.criteria?.high_short_interest && (
                        <span className="badge badge-red text-xs">High SI</span>
                      )}
                      {c.criteria?.high_days_to_cover && (
                        <span className="badge badge-yellow text-xs">High DTC</span>
                      )}
                      {c.criteria?.very_high_risk && (
                        <span className="badge badge-red text-xs">Extreme</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ─── ETF Flows ───────────────────────────────────────────────────── */}
      <Section icon={ArrowUpDown} title="ETF Flows & Sector Rotation">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Sector rotation heatmap — backend sends avg_change_pct (ETF price
              performance as a flow proxy), NOT dollar flows. Display % honestly
              and derive colour from the same field the signal is derived from. */}
          <div className="card p-4 lg:col-span-2">
            <h3 className="panel-title mb-3">Sector Rotation (7-Day ETF Price Performance)</h3>
            <p className="text-xs text-text-muted mb-3">ETF price change used as a proxy for fund flows — not actual flow dollars.</p>
            {sectorRotation.length === 0 ? (
              <p className="text-sm text-text-muted">No sector rotation data.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {sectorRotation.map((s, i) => {
                  const change = s.avg_change_pct
                  const isInflow = (change ?? 0) > 0
                  return (
                    <div
                      key={i}
                      className={`p-3 rounded-lg border ${
                        isInflow
                          ? 'bg-emerald/5 border-emerald/20'
                          : 'bg-rose/5 border-rose/20'
                      }`}
                    >
                      <p className="text-xs font-medium text-text-primary">{s.sector}</p>
                      <p className={`text-lg font-bold ${isInflow ? 'text-emerald' : 'text-rose-light'}`}>
                        {change != null ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%` : '—'}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs font-medium ${isInflow ? 'text-emerald' : 'text-rose-light'}`}>
                          {s.signal}
                        </span>
                        {s.top_etf && <span className="text-xs text-text-muted font-mono">{s.top_etf}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Inflows / Outflows lists */}
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="panel-title mb-3 flex items-center gap-1.5">
                <TrendingUp size={13} className="text-emerald" aria-hidden /> Top Inflows
              </h3>
              {etfInflows.length === 0 ? (
                <p className="text-xs text-text-muted">No data.</p>
              ) : (
                <div className="space-y-1.5">
                  {/* Backend sends price_change_pct (flow proxy), not flow_m dollars */}
                  {etfInflows.slice(0, 8).map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <TickerLink ticker={f.ticker} />
                        <span className="text-text-muted text-xs truncate max-w-24">{f.name}</span>
                      </div>
                      <span className="text-emerald text-xs font-medium">
                        {f.price_change_pct != null ? `+${f.price_change_pct.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card p-4">
              <h3 className="panel-title mb-3 flex items-center gap-1.5">
                <TrendingDown size={13} className="text-rose-light" aria-hidden /> Top Outflows
              </h3>
              {etfOutflows.length === 0 ? (
                <p className="text-xs text-text-muted">No data.</p>
              ) : (
                <div className="space-y-1.5">
                  {/* Backend sends price_change_pct (flow proxy), not flow_m dollars */}
                  {etfOutflows.slice(0, 8).map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <TickerLink ticker={f.ticker} />
                        <span className="text-text-muted text-xs truncate max-w-24">{f.name}</span>
                      </div>
                      <span className="text-rose-light text-xs font-medium">
                        {f.price_change_pct != null ? `${f.price_change_pct.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ─── Political Intelligence ───────────────────────────────────────── */}
      <Section icon={Landmark} title="Political Intelligence">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Congressional trades table */}
          <div className="card p-4 lg:col-span-2 overflow-x-auto">
            <h3 className="panel-title mb-3">Recent Congressional Trades</h3>
            {congressTrades.length === 0 ? (
              <p className="text-sm text-text-muted">
                No congressional disclosures loaded yet — the feed pulls Senate eFD
                trades and House Clerk filings, and refreshes every 6 hours.
              </p>
            ) : (
              <table className="w-full text-sm table-pro">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Ticker</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {congressTrades.slice(0, 20).map((t, i) => (
                    <tr key={i}>
                      <td className="py-2 pr-3 text-text-primary whitespace-nowrap">
                        {t.member_name}
                        {t.district ? <span className="text-text-muted text-xs ml-1">({t.district})</span> : null}
                      </td>
                      {/* House Clerk rows are filing EVENTS — the transactions
                          exist only inside a scanned PDF, so ticker/type/amount
                          are unknown. Say so and link the document rather than
                          rendering three blank cells that look like a bug. */}
                      {t.filing_only ? (
                        <td className="py-2 pr-3 text-text-muted text-xs" colSpan={3}>
                          Filed a periodic transaction report — individual trades are in the
                          {t.document_url ? (
                            <a href={t.document_url} target="_blank" rel="noopener noreferrer"
                               className="text-blue-light hover:underline ml-1">filing PDF</a>
                          ) : ' filing PDF'}
                        </td>
                      ) : (
                        <>
                          <td className="py-2 pr-3"><TickerLink ticker={t.ticker} /></td>
                          <td className="py-2 pr-3">
                            <span className={`badge text-xs ${
                              ['purchase', 'buy'].some((k) => t.transaction_type?.toLowerCase().includes(k)) ? 'badge-green' : 'badge-red'
                            }`}>
                              {t.transaction_type}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-text-secondary">{t.amount_range ?? t.amount ?? '—'}</td>
                        </>
                      )}
                      <td className="py-2 text-text-muted text-xs">{t.transaction_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-[11px] text-text-muted mt-3">
              Sources: U.S. Senate eFD (ticker-level trades) and the House Clerk's bulk
              disclosures. The Senate site blocks our server's network, so on most days
              you'll see House filings here — the House publishes transactions as scanned
              PDFs, so those rows link the document instead of naming a ticker.
            </p>
          </div>

          {/* Top bought / sold */}
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="panel-title mb-3 flex items-center gap-1.5">
                <TrendingUp size={13} className="text-emerald" aria-hidden /> Top Bought by Congress
              </h3>
              {congressTopBought.length === 0 ? (
                <p className="text-xs text-text-muted">No data.</p>
              ) : (
                <div className="space-y-1.5">
                  {congressTopBought.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <TickerLink ticker={t.ticker} />
                      <span className="text-emerald text-xs font-medium">{t.count} trades</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card p-4">
              <h3 className="panel-title mb-3 flex items-center gap-1.5">
                <TrendingDown size={13} className="text-rose-light" aria-hidden /> Top Sold by Congress
              </h3>
              {congressTopSold.length === 0 ? (
                <p className="text-xs text-text-muted">No data.</p>
              ) : (
                <div className="space-y-1.5">
                  {congressTopSold.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <TickerLink ticker={t.ticker} />
                      <span className="text-rose-light text-xs font-medium">{t.count} trades</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gov contracts */}
        <div className="card p-4">
          <h3 className="panel-title mb-3">Government Contract Winners</h3>
          {govContracts.length === 0 ? (
            <p className="text-sm text-text-muted">No contract data available.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {govContracts.slice(0, 9).map((c, i) => (
                <div key={i} className="p-3 rounded-lg bg-surface-hover/50 border border-border/50">
                  <div className="flex items-center justify-between mb-1">
                    {/* Backend field is company_name, not company */}
                    <span className="text-sm font-semibold text-text-primary">{c.company_name ?? '—'}</span>
                    {c.ticker && <TickerLink ticker={c.ticker} className="text-xs" />}
                  </div>
                  <p className="text-lg font-bold text-emerald">{fmtUsd(c.award_amount)}</p>
                  <p className="text-xs text-text-muted mt-1 line-clamp-2">{c.agency} &mdash; {c.description}</p>
                  <p className="text-xs text-text-muted mt-1">{c.award_date}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ─── Lobbying Intelligence ────────────────────────────────────────── */}
      <Section icon={DollarSign} title="Lobbying Intelligence">
        <div className="card p-4 overflow-x-auto">
          <h3 className="panel-title mb-3">Top Corporate Lobbying Spenders</h3>
          <p className="text-xs text-text-muted mb-3">Increasing lobbying spend often precedes regulatory changes affecting stock price</p>
          {lobbyingSpenders.length === 0 ? (
            <p className="text-sm text-text-muted">No lobbying data available.</p>
          ) : (
            <table className="w-full text-sm table-pro">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Ticker</th>
                  <th className="!text-right">Total Spend</th>
                  <th className="!text-right">Filings</th>
                  <th>Key Issues</th>
                </tr>
              </thead>
              <tbody>
                {lobbyingSpenders.slice(0, 15).map((s, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-3 text-text-primary whitespace-nowrap">{s.company}</td>
                    <td className="py-2 pr-3">{s.ticker ? <TickerLink ticker={s.ticker} /> : '-'}</td>
                    <td className="py-2 pr-3 text-right text-emerald font-medium">{fmtUsd(s.total_spend)}</td>
                    <td className="py-2 pr-3 text-right text-text-secondary">{s.filing_count}</td>
                    <td className="py-2 text-text-muted text-xs">
                      {(s.issues ?? []).slice(0, 3).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Section>

      {/* ─── Patent Intelligence ──────────────────────────────────────────── */}
      <Section icon={Lightbulb} title="Patent Intelligence">
        <div className="card p-4">
          <h3 className="panel-title mb-3">Top Patent Innovators (90 Days)</h3>
          <p className="text-xs text-text-muted mb-3">Companies filing more patents often have stronger R&amp;D pipelines and innovation moats</p>
          {topInnovators.length === 0 ? (
            <p className="text-sm text-text-muted">No patent data available.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {topInnovators.slice(0, 12).map((inv, i) => (
                <div key={i} className="p-3 rounded-lg bg-surface-hover/50 border border-border/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-text-primary">{inv.company}</span>
                    {inv.ticker && <TickerLink ticker={inv.ticker} className="text-xs" />}
                  </div>
                  <p className="text-lg font-bold text-blue-light">{inv.count} patents</p>
                  {inv.recent_patents?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {inv.recent_patents.slice(0, 2).map((p, j) => (
                        <p key={j} className="text-xs text-text-muted line-clamp-1">{p}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ─── Corporate Flights ────────────────────────────────────────────── */}
      <Section icon={Plane} title="Corporate Jet Tracking">
        <div className="card p-4">
          <h3 className="panel-title mb-3">Unusual Flight Patterns</h3>
          <p className="text-xs text-text-muted mb-3">Corporate jets near financial hubs can signal M&amp;A or deal activity</p>
          {unusualFlights.length === 0 ? (
            <p className="text-sm text-text-muted">No unusual flight patterns detected.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {unusualFlights.slice(0, 9).map((f, i) => (
                <div key={i} className={`p-3 rounded-lg border ${
                  f.signal_strength === 'high'
                    ? 'bg-rose/5 border-rose/20'
                    : f.signal_strength === 'medium'
                    ? 'bg-amber/5 border-amber/20'
                    : 'bg-blue/5 border-blue/20'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-text-primary">{f.company}</span>
                      <TickerLink ticker={f.ticker} className="text-xs" />
                    </div>
                    <span className={`badge text-xs ${signalBadge(f.signal_strength)}`}>
                      {f.signal_strength}
                    </span>
                  </div>
                  <p className="text-xs text-text-secondary mt-1">{f.signal}</p>
                  <p className="text-xs text-text-muted mt-1">{f.interpretation}</p>
                  {f.simulated && (
                    <span className="text-xs text-text-muted italic mt-1 block">Simulated data</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ─── Insider Intelligence ─────────────────────────────────────────── */}
      <Section icon={Users} title="Insider Intelligence">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Large insider trades */}
          <div className="card p-4 overflow-x-auto">
            <h3 className="panel-title mb-3">Recent Large Insider Trades</h3>
            {(Array.isArray(insiderLarge) ? insiderLarge : []).length === 0 ? (
              <p className="text-sm text-text-muted">No large insider trades available.</p>
            ) : (
              <table className="w-full text-sm table-pro">
                <thead>
                  <tr>
                    <th>Insider</th>
                    <th>Ticker</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(insiderLarge) ? insiderLarge : []).slice(0, 15).map((t, i) => (
                    <tr key={i}>
                      <td className="py-2 pr-3 text-text-primary whitespace-nowrap">
                        {t.insider_name}
                        <span className="text-text-muted text-xs ml-1">({t.title})</span>
                      </td>
                      <td className="py-2 pr-3"><TickerLink ticker={t.ticker} /></td>
                      <td className="py-2 pr-3">
                        <span className={`badge text-xs ${
                          t.transaction_type?.toLowerCase().includes('purchase') || t.transaction_type?.toLowerCase().includes('buy')
                            ? 'badge-green' : 'badge-red'
                        }`}>
                          {t.transaction_type}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-text-secondary">{fmtUsd(t.value)}</td>
                      <td className="py-2 text-text-muted text-xs">{t.filing_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Cluster buys */}
          <div className="card p-4">
            <h3 className="panel-title mb-3 flex items-center gap-1.5">
              <AlertTriangle size={13} className="text-amber" aria-hidden /> Cluster Buys (Strong Signal)
            </h3>
            <p className="text-xs text-text-muted mb-3">Multiple insiders buying the same stock</p>
            {(Array.isArray(insiderClusters) ? insiderClusters : []).length === 0 ? (
              <p className="text-sm text-text-muted">No cluster buys detected.</p>
            ) : (
              <div className="space-y-3">
                {(Array.isArray(insiderClusters) ? insiderClusters : []).slice(0, 8).map((c, i) => (
                  <div key={i} className="p-3 rounded-lg bg-amber/5 border border-amber/20">
                    <div className="flex items-center justify-between mb-1">
                      <TickerLink ticker={c.ticker} className="font-semibold" />
                      <span className="text-amber text-xs font-bold">{c.insider_count} insiders buying</span>
                    </div>
                    <p className="text-xs text-text-secondary">{c.company}</p>
                    <p className="text-sm text-emerald font-medium">{fmtUsd(c.total_value)} total</p>
                    {c.insiders && (
                      <div className="mt-2 space-y-0.5">
                        {c.insiders.slice(0, 3).map((ins, j) => (
                          <p key={j} className="text-xs text-text-muted">
                            {ins.name} ({ins.title}) &mdash; {fmtUsd(ins.value)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Guru portfolios */}
        <div>
          <h3 className="panel-title mb-3">Guru Portfolios (13F Filings)</h3>
          <GuruTabs portfolios={Array.isArray(guruPortfolios) ? guruPortfolios : []} />
        </div>
      </Section>

      {/* ─── Macro & Fed ──────────────────────────────────────────────────── */}
      <Section icon={BarChart3} title="Macro & Fed Signals">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Key indicators grid */}
          <div className="card p-4 lg:col-span-2">
            <h3 className="panel-title mb-3">Key Economic Indicators</h3>
            {fedIndicators.length === 0 ? (
              <p className="text-sm text-text-muted">No indicator data available.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {fedIndicators.slice(0, 9).map((ind, i) => (
                  <div key={i} className="stat-card">
                    <p className="stat-label flex items-center gap-1">
                      {ind.name}
                      {ind.source === 'fallback' && (
                        <span
                          className="px-1 rounded bg-amber/15 text-amber-300 border border-amber/30 text-[9px] uppercase tracking-wide"
                          title={`FRED unavailable — last-known value as of ${ind.date}`}
                        >
                          stale
                        </span>
                      )}
                    </p>
                    <p className="stat-value text-lg">{typeof ind.value === 'number' ? (ind.value ?? 0).toFixed(2) : ind.value}</p>
                    <p className="text-xs text-text-muted">{ind.unit} &mdash; {ind.date}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recession + FOMC */}
          <div className="space-y-4">
            {/* Recession signal */}
            <div className="card p-4">
              <h3 className="panel-title mb-3">Recession Risk</h3>
              {recessionSignals && recessionSignals.recession_risk != null ? (
                <>
                  {/* Backend fields: recession_risk + risk_score (0-7 scale),
                      signals is a list of plain-string warnings */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${riskColor(recessionSignals.recession_risk)}`}>
                      {recessionSignals.recession_risk}
                    </span>
                    <span className="text-text-muted text-xs">Score: {recessionSignals.risk_score ?? 0}/7</span>
                  </div>
                  {(recessionSignals.signals ?? []).length === 0 ? (
                    <p className="text-xs text-text-muted">No warning signals active.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(recessionSignals.signals ?? []).slice(0, 4).map((s, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className="w-2 h-2 rounded-full shrink-0 bg-amber mt-0.5" />
                          <span className="text-text-secondary">{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-text-muted">No recession data.</p>
              )}
            </div>

            {/* FOMC countdown */}
            <div className="card p-4">
              <h3 className="panel-title mb-2">Next FOMC Meeting</h3>
              {nextFomc ? (
                <div className="text-center py-2">
                  <p className="text-3xl font-bold text-blue-light">{nextFomc.days_until}</p>
                  <p className="text-xs text-text-muted">days until {nextFomc.date}</p>
                  <p className="text-xs text-text-secondary mt-1">{nextFomc.type}</p>
                </div>
              ) : (
                <p className="text-sm text-text-muted">No upcoming meetings.</p>
              )}
            </div>
          </div>
        </div>

        {/* Yield curve chart */}
        <div className="card p-4">
          <h3 className="panel-title mb-3">Yield Curve</h3>
          {yieldCurve.length === 0 ? (
            <p className="text-sm text-text-muted">No yield curve data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={yieldCurve} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="maturity" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  tickFormatter={v => `${v}%`}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <Tooltip
                  formatter={(v: number) => [`${v.toFixed(2)}%`, 'Rate']}
                  contentStyle={{ background: 'rgb(var(--bg-surface))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--text-primary))', borderRadius: 8 }}
                />
                <Bar dataKey="rate" radius={[4, 4, 0, 0]}>
                  {yieldCurve.map((_, i) => (
                    <Cell key={i} fill={i < yieldCurve.length / 2 ? '#f59e0b' : '#3b82f6'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      {/* ─── Social Sentiment ─────────────────────────────────────────────── */}
      <Section icon={Brain} title="Social Sentiment">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Fear & Greed gauge */}
          <div className="card p-4 flex flex-col items-center justify-center">
            <h3 className="panel-title mb-4">Fear &amp; Greed Index</h3>
            {fearGreedValue !== null ? (
              <>
                <div
                  className="w-28 h-28 rounded-full border-4 flex items-center justify-center mb-3"
                  style={{ borderColor: fearGreedColor(fearGreedValue) }}
                >
                  <span className="text-3xl font-bold" style={{ color: fearGreedColor(fearGreedValue) }}>
                    {fearGreedValue}
                  </span>
                </div>
                <span
                  className="text-sm font-semibold uppercase tracking-wide"
                  style={{ color: fearGreedColor(fearGreedValue) }}
                >
                  {fearGreedLabel}
                </span>
                {/* Gradient bar */}
                <div className="w-full mt-4 h-2 rounded-full overflow-hidden"
                  style={{ background: 'linear-gradient(to right, #f43f5e, #f59e0b, #94a3b8, #22c55e, #10b981)' }}
                >
                  <div className="relative h-full">
                    <div
                      className="absolute top-[-2px] w-3 h-3 rounded-full bg-text-primary border-2 border-base"
                      style={{ left: `${fearGreedValue}%`, transform: 'translateX(-50%)' }}
                    />
                  </div>
                </div>
                <div className="flex justify-between w-full text-xs text-text-muted mt-1">
                  <span>Extreme Fear</span><span>Extreme Greed</span>
                </div>
              </>
            ) : (
              <p className="text-sm text-text-muted">No data.</p>
            )}
          </div>

          {/* WSB trending */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">WSB Trending</h3>
            {wsbPosts.length === 0 ? (
              <p className="text-sm text-text-muted">No WSB data.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {wsbPosts.slice(0, 10).map((p, i) => (
                  <a
                    key={i}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-2 rounded-lg hover:bg-surface-hover/50 transition-colors"
                  >
                    <p className="text-sm text-text-primary line-clamp-2">{p.title}</p>
                    <div className="flex items-center gap-3 text-xs text-text-muted mt-1">
                      <span>{p.score} pts</span>
                      <span>{p.num_comments} comments</span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Trending tickers */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Trending Tickers (Reddit)</h3>
            {trendingTickers.length === 0 ? (
              <p className="text-sm text-text-muted">No trending data.</p>
            ) : (
              <div className="space-y-2">
                {trendingTickers.slice(0, 12).map((t, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-muted w-5">{i + 1}.</span>
                      <TickerLink ticker={t.ticker} className="font-medium" />
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-text-secondary">{t.mentions} mentions</span>
                      {/* Backend field is avg_score, not score */}
                      <span className={`text-xs font-medium ${(t.avg_score ?? 0) >= 0 ? 'text-emerald' : 'text-rose-light'}`}>
                        {t.avg_score != null ? `${t.avg_score >= 0 ? '+' : ''}${t.avg_score.toFixed(1)}` : '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ─── Google Trends & Cramer ──────────────────────────────────────── */}
      <Section icon={TrendingUp} title="Google Trends & Cramer Tracker">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Trending searches */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Google Trending Searches</h3>
            {googleTrending.length === 0 ? (
              <p className="text-sm text-text-muted">No trending data available.</p>
            ) : (
              <div className="grid grid-cols-2 gap-1 max-h-80 overflow-y-auto">
                {googleTrending.slice(0, 20).map((term, i) => (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded hover:bg-surface-hover/50 transition-colors">
                    <span className="text-xs text-text-muted w-5 text-right">{i + 1}.</span>
                    <span className="text-sm text-text-primary truncate">{term}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cramer picks */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Jim Cramer Picks (Inverse Cramer)</h3>
            {cramerPicks.length === 0 ? (
              <p className="text-sm text-text-muted">No Cramer picks available.</p>
            ) : (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {cramerPicks.slice(0, 15).map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-surface-hover/30 border border-border/30">
                    <div className="flex items-center gap-2">
                      <TickerLink ticker={p.ticker} className="text-sm" />
                      <span className="text-xs text-text-secondary truncate max-w-[120px]">{p.company}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge text-xs ${p.call === 'buy' ? 'badge-green' : 'badge-red'}`}>
                        {p.call?.toUpperCase()}
                      </span>
                      <span className="text-xs text-text-muted">{p.date?.slice(0, 10)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ─── Stock Splits ──────────────────────────────────────────────────── */}
      {recentSplits.length > 0 && (
        <Section icon={ArrowUpDown} title="Stock Splits">
          <div className="card p-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentSplits.slice(0, 9).map((s, i) => (
                <div key={i} className="p-3 rounded-lg bg-surface-hover/30 border border-border/30">
                  <div className="flex items-center justify-between mb-1">
                    <TickerLink ticker={s.ticker} className="text-sm font-semibold" />
                    <span className={`badge text-xs ${s.type === 'forward' ? 'badge-green' : 'badge-yellow'}`}>
                      {s.type}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-text-primary">{s.ratio}</div>
                  {/* Backend sends a single `date` field for recent splits */}
                  <div className="text-xs text-text-muted mt-0.5">{s.date ?? '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ─── News & Events ────────────────────────────────────────────────── */}
      <Section icon={Newspaper} title="News & Events">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Headlines */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Latest Headlines</h3>
            {headlines.length === 0 ? (
              <p className="text-sm text-text-muted">No headlines available.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {headlines.map((h, i) => (
                  <a
                    key={i}
                    href={h.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 p-2 rounded-lg hover:bg-surface-hover/50 transition-colors group"
                  >
                    <ChevronRight size={12} className="text-text-muted mt-1 shrink-0 group-hover:text-blue-light" aria-hidden />
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary group-hover:text-blue-light line-clamp-2">{h.title}</p>
                      <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
                        <span>{h.source}</span>
                        {h.published_at && (
                          <>
                            <span>&middot;</span>
                            <span>{formatDistanceToNow(new Date(h.published_at), { addSuffix: true })}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <ExternalLink size={11} className="text-text-muted shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Material events */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Material Events (8-K Filings)</h3>
            {materialEvents.length === 0 ? (
              <p className="text-sm text-text-muted">No recent material events.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {/* Backend 8-K events have NO ticker; fields are type/summary */}
                {materialEvents.map((e, i) => (
                  <div key={i} className="p-3 rounded-lg bg-surface-hover/50 border border-border/50">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <span className="text-text-primary text-sm font-medium truncate">{e.company || '—'}</span>
                      <span className="badge badge-blue text-xs shrink-0">{e.type ?? '8-K'}</span>
                    </div>
                    <p className="text-xs text-text-muted line-clamp-2">{e.summary || e.title || ''}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-text-muted">{e.filing_date}</span>
                      {e.url && (
                        <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-light hover:underline flex items-center gap-1">
                          Filing <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  )
}
